import { apiRequest } from '@/utils/api-request';
import type { ContextSwitchKind } from './context-switch-kinds';
import type {
  Agent,
  AgentCreateRequest,
  AgentCreateResponse,
  AgentListResponse,
  AgentResponse,
  AgentUpdateRequest,
  A2ATokenRotateResponse,
  AutoGenerateAgentDescriptionRequest,
  AutoGenerateAgentDescriptionResponse,
  PeerAgent,
  PeerAgentCreateRequest,
  PeerAgentListResponse,
  PeerAgentResponse,
  PeerAgentUpdateRequest,
} from '@/types/agent-types';

export function unwrap<T>(response: unknown): T | null {
  if (!response) return null;
  const res = response as Record<string, unknown>;
  // apiRequest resolves non-2xx as { success: false, data: <error-body> }, so
  // without this guard the caller reads the error body as the success shape and
  // trips on undefined nested fields.
  if ('success' in res && res.success === false) {
    const detail =
      typeof res.detail === 'string'
        ? res.detail
        : typeof res.message === 'string'
          ? res.message
          : 'Request failed';
    throw new Error(detail);
  }
  if ('data' in res && res.data) return res.data as T;
  return response as T;
}

export async function listAgents(orgId: string): Promise<Agent[]> {
  const response = await apiRequest<AgentListResponse>({
    url: `/agents/${orgId}`,
    method: 'GET',
    target: 'platform',
  });
  const data = unwrap<AgentListResponse>(response);
  return data?.agents ?? [];
}

export async function getAgent(
  orgId: string,
  agentId: string,
): Promise<Agent | null> {
  const response = await apiRequest<AgentResponse>({
    url: `/agents/${orgId}/${agentId}`,
    method: 'GET',
    target: 'platform',
  });
  const data = unwrap<AgentResponse>(response);
  return data?.agent ?? null;
}

export async function getAgentDetail(
  orgId: string,
  agentId: string,
): Promise<AgentResponse | null> {
  const response = await apiRequest<AgentResponse>({
    url: `/agents/${orgId}/${agentId}`,
    method: 'GET',
    target: 'platform',
  });
  return unwrap<AgentResponse>(response);
}

export async function createAgent(
  orgId: string,
  data: AgentCreateRequest,
): Promise<AgentCreateResponse | null> {
  const response = await apiRequest<AgentCreateResponse>({
    url: `/agents/${orgId}`,
    method: 'POST',
    target: 'platform',
    body: data as unknown as Record<string, unknown>,
  });
  return unwrap<AgentCreateResponse>(response);
}

export async function updateAgent(
  orgId: string,
  agentId: string,
  data: AgentUpdateRequest,
): Promise<AgentResponse | null> {
  const response = await apiRequest<AgentResponse>({
    url: `/agents/${orgId}/${agentId}`,
    method: 'PATCH',
    target: 'platform',
    body: data as unknown as Record<string, unknown>,
  });
  return unwrap<AgentResponse>(response);
}

export async function deleteAgent(
  orgId: string,
  agentId: string,
): Promise<void> {
  await apiRequest({
    url: `/agents/${orgId}/${agentId}`,
    method: 'DELETE',
    target: 'platform',
  });
}

// ``name`` is capped at 100 chars by the platform-api schema; reserve
// room for the "Copy of " prefix so duplicating a near-max-length name
// doesn't 422 with a validation error from the create endpoint.
const _NAME_MAX_LENGTH = 100;
const _COPY_PREFIX = 'Copy of ';

function _copyName(sourceName: string): string {
  const trimmed = (sourceName || 'Untitled agent').trim();
  const candidate = `${_COPY_PREFIX}${trimmed}`;
  if (candidate.length <= _NAME_MAX_LENGTH) return candidate;
  const room = _NAME_MAX_LENGTH - _COPY_PREFIX.length - 1; // -1 for ellipsis
  return `${_COPY_PREFIX}${trimmed.slice(0, room)}…`;
}

/**
 * Build a create payload from an existing agent.
 *
 * Exposed (rather than inlined) so the list page can compose its own
 * call -- and so future call sites (e.g. a "duplicate" affordance on
 * the agent config page itself) get the same field mapping for free.
 * Server-side concerns (bearer token, ids, timestamps, runtime canvas
 * id) are intentionally excluded; the create endpoint mints them.
 */
export function buildDuplicateAgentRequest(
  source: Agent,
  overrides: Partial<AgentCreateRequest> = {},
): AgentCreateRequest {
  return {
    name: _copyName(source.name),
    description: source.description ?? '',
    icon: source.icon ?? '',
    color: source.color ?? '',
    model_provider: source.model_provider,
    model_name: source.model_name,
    system_prompt: source.system_prompt,
    max_tokens: source.max_tokens,
    temperature: source.temperature,
    top_p: source.top_p,
    thinking_enabled: source.thinking_enabled,
    thinking_budget_tokens: source.thinking_budget_tokens,
    enable_workflow_runtime: source.enable_workflow_runtime,
    enable_workflow_authoring: source.enable_workflow_authoring,
    enable_a2a_outbound: source.enable_a2a_outbound,
    render_files_in_chat: source.render_files_in_chat,
    show_intermediate_files: source.show_intermediate_files,
    allow_user_workflows: source.allow_user_workflows,
    enable_github_tools: source.enable_github_tools,
    memory_enabled: source.memory_enabled,
    memory_retention_days: source.memory_retention_days,
    enable_task_alignment_guardrail: source.enable_task_alignment_guardrail,
    task_alignment_min_relevance: source.task_alignment_min_relevance,
    e2b_enabled: source.e2b_enabled,
    e2b_template: source.e2b_template,
    e2b_run_timeout_s: source.e2b_run_timeout_s,
    e2b_max_edit_calls_per_turn: source.e2b_max_edit_calls_per_turn,
    e2b_allow_internet_access: source.e2b_allow_internet_access,
    e2b_sandbox_env: source.e2b_sandbox_env
      ? { ...source.e2b_sandbox_env }
      : undefined,
    allow_synthetic_data_generation: source.allow_synthetic_data_generation,
    workflow_allowlist: [...(source.workflow_allowlist ?? [])],
    workflow_allowlist_labels: [...(source.workflow_allowlist_labels ?? [])],
    mcp_tool_allowlist: [...(source.mcp_tool_allowlist ?? [])],
    data_collection_allowlist: [...(source.data_collection_allowlist ?? [])],
    guardrail_allowlist: (source.guardrail_allowlist ?? []).map((g) => ({
      ...g,
    })),
    peer_agent_allowlist: [...(source.peer_agent_allowlist ?? [])],
    custom_skills: (source.custom_skills ?? []).map((s) => ({
      ...s,
      tags: [...(s.tags ?? [])],
      input_mapping: s.input_mapping ? { ...s.input_mapping } : null,
    })),
    skill_overrides: Object.fromEntries(
      Object.entries(source.skill_overrides ?? {}).map(([k, v]) => [
        k,
        {
          ...v,
          tags: v.tags ? [...v.tags] : null,
          input_mapping: v.input_mapping ? { ...v.input_mapping } : null,
        },
      ]),
    ),
    exposed_via_a2a: source.exposed_via_a2a,
    ...overrides,
  };
}

/**
 * Duplicate an agent: fetches the latest record (so the duplicate
 * never lags behind an unsaved edit cached in the list view) and
 * POSTs a fresh create with the same configuration under a renamed
 * "Copy of …" name. The server mints a new id, bearer token, A2A
 * endpoint, and runtime canvas for the duplicate.
 *
 * If ``sourceAgent`` is supplied, the fetch is skipped -- callers
 * with a fresh record in scope (e.g. just-loaded list rows) can
 * pass it through to avoid the extra round-trip.
 */
export async function duplicateAgent(
  orgId: string,
  agentId: string,
  options: {
    sourceAgent?: Agent;
    overrides?: Partial<AgentCreateRequest>;
  } = {},
): Promise<AgentCreateResponse | null> {
  const source = options.sourceAgent ?? (await getAgent(orgId, agentId));
  if (!source) {
    throw new Error(`Source agent ${agentId} not found`);
  }
  const payload = buildDuplicateAgentRequest(source, options.overrides);
  return createAgent(orgId, payload);
}

export async function autoGenerateAgentDescription(
  orgId: string,
  payload: AutoGenerateAgentDescriptionRequest,
): Promise<string | null> {
  const response = await apiRequest<AutoGenerateAgentDescriptionResponse>({
    url: `/agents/${orgId}/auto_generate_description`,
    method: 'POST',
    // Cast: ``AutoGenerateAgentDescriptionRequest`` is a typed
    // interface; apiRequest expects a ``Record<string, unknown>``
    // for the body.  The shape is JSON-serializable, so this is a
    // structural-typing-only cast.
    body: payload as unknown as Record<string, unknown>,
    target: 'platform',
  });
  const data = unwrap<AutoGenerateAgentDescriptionResponse>(response);
  return data?.description ?? null;
}

export async function rotateA2AToken(
  orgId: string,
  agentId: string,
): Promise<A2ATokenRotateResponse | null> {
  const response = await apiRequest<A2ATokenRotateResponse>({
    url: `/agents/${orgId}/${agentId}/rotate-a2a-token`,
    method: 'POST',
    target: 'platform',
  });
  return unwrap<A2ATokenRotateResponse>(response);
}

export async function revokeA2AToken(
  orgId: string,
  agentId: string,
): Promise<void> {
  await apiRequest({
    url: `/agents/${orgId}/${agentId}/a2a-token`,
    method: 'DELETE',
    target: 'platform',
  });
}

// ---------------------------------------------------------------------------
// Multi-bearer
// ---------------------------------------------------------------------------

export interface A2ABearerRecord {
  bearer_id: string;
  agent_id: string;
  orgid: string;
  a2a_bearer_token_prefix: string;
  label: string;
  created_at: string;
  created_by: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface A2ABearerCreateResponse {
  success: boolean;
  message: string;
  bearer: A2ABearerRecord;
  a2a_bearer_token: string;
}

interface A2ABearerListResponse {
  success: boolean;
  bearers: A2ABearerRecord[];
  count: number;
}

export async function listAgentBearers(
  orgId: string,
  agentId: string,
): Promise<A2ABearerRecord[]> {
  const response = await apiRequest<A2ABearerListResponse>({
    url: `/agents/${orgId}/${agentId}/bearers`,
    method: 'GET',
    target: 'platform',
  });
  return unwrap<A2ABearerListResponse>(response)?.bearers ?? [];
}

export async function createAgentBearer(
  orgId: string,
  agentId: string,
  label: string,
): Promise<A2ABearerCreateResponse | null> {
  const response = await apiRequest<A2ABearerCreateResponse>({
    url: `/agents/${orgId}/${agentId}/bearers`,
    method: 'POST',
    body: { label },
    target: 'platform',
  });
  return unwrap<A2ABearerCreateResponse>(response);
}

export async function revokeAgentBearer(
  orgId: string,
  agentId: string,
  bearerId: string,
): Promise<void> {
  await apiRequest({
    url: `/agents/${orgId}/${agentId}/bearers/${bearerId}`,
    method: 'DELETE',
    target: 'platform',
  });
}

export async function listPeerAgents(orgId: string): Promise<PeerAgent[]> {
  const response = await apiRequest<PeerAgentListResponse>({
    url: `/agents/${orgId}/peer-agents`,
    method: 'GET',
    target: 'platform',
  });
  return unwrap<PeerAgentListResponse>(response)?.peer_agents ?? [];
}

export async function getPeerAgent(
  orgId: string,
  peerId: string,
): Promise<PeerAgent | null> {
  const response = await apiRequest<PeerAgentResponse>({
    url: `/agents/${orgId}/peer-agents/${peerId}`,
    method: 'GET',
    target: 'platform',
  });
  return unwrap<PeerAgentResponse>(response)?.peer_agent ?? null;
}

export async function createPeerAgent(
  orgId: string,
  data: PeerAgentCreateRequest,
): Promise<PeerAgent | null> {
  const response = await apiRequest<PeerAgentResponse>({
    url: `/agents/${orgId}/peer-agents`,
    method: 'POST',
    target: 'platform',
    body: data as unknown as Record<string, unknown>,
  });
  return unwrap<PeerAgentResponse>(response)?.peer_agent ?? null;
}

export async function updatePeerAgent(
  orgId: string,
  peerId: string,
  data: PeerAgentUpdateRequest,
): Promise<PeerAgent | null> {
  const response = await apiRequest<PeerAgentResponse>({
    url: `/agents/${orgId}/peer-agents/${peerId}`,
    method: 'PATCH',
    target: 'platform',
    body: data as unknown as Record<string, unknown>,
  });
  return unwrap<PeerAgentResponse>(response)?.peer_agent ?? null;
}

export async function deletePeerAgent(
  orgId: string,
  peerId: string,
): Promise<void> {
  await apiRequest({
    url: `/agents/${orgId}/peer-agents/${peerId}`,
    method: 'DELETE',
    target: 'platform',
  });
}

export interface ChatResponse {
  run_id?: string;
  session_id?: string;
  status?: string;
  agent_id: string;
  canvas_workflow_id?: string;
  conversation_id: string;
  // Populated by the inline LLM fast-path (Sprint 2 v1 -- bypasses the
  // empty agentic-backend canvas).  When set the FE can render the
  // reply immediately without polling.  Mirrors platform-api's
  // ``schemas/agents.py::AgentChatResponse``.
  assistant_reply?: string | null;
}

export async function chatWithAgent(
  orgId: string,
  agentId: string,
  message: string,
  options: {
    conversationId?: string | null;
    metadata?: Record<string, unknown>;
  } = {},
): Promise<ChatResponse | null> {
  const response = await apiRequest<ChatResponse>({
    url: `/agents/${orgId}/${agentId}/chat`,
    method: 'POST',
    target: 'platform',
    body: {
      message,
      conversation_id: options.conversationId ?? null,
      metadata: options.metadata ?? {},
    },
  });
  return unwrap<ChatResponse>(response);
}

export interface AgentChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
}

export type ConversationRole = 'owner' | 'viewer';

export interface AgentChatMessagesResponse {
  success: boolean;
  conversation_id: string;
  agent_id: string;
  user_role?: ConversationRole;
  owner_email?: string;
  title?: string;
  messages: AgentChatMessage[];
  count: number;
}

export interface AgentConversationSummary {
  conversation_id: string;
  agent_id: string;
  user_email: string;
  created_at?: string;
  last_message_at?: string;
  preview?: string;
  title?: string;
  user_role?: ConversationRole;
  owner_email?: string;
  /** Workflow ids this conversation touched, accumulated across turns. */
  workflow_ids?: string[];
  /** Artifact ids this conversation touched, accumulated across turns. */
  artifact_ids?: string[];
}

export interface AgentConversationsListResponse {
  success: boolean;
  agent_id: string;
  conversations: AgentConversationSummary[];
  count: number;
  total?: number;
  limit?: number | null;
  offset?: number;
}

export type ConversationListScope = 'mine' | 'shared';

export const CONVERSATION_SORT_KEYS = [
  'last_message_desc',
  'last_message_asc',
  'title_asc',
  'title_desc',
] as const;

export type ConversationSortKey = (typeof CONVERSATION_SORT_KEYS)[number];

export interface ListAgentConversationsOptions {
  scope?: ConversationListScope;
  search?: string;
  sort?: ConversationSortKey;
  limit?: number;
  offset?: number;
}

export interface AgentConversationsPage {
  conversations: AgentConversationSummary[];
  total: number;
}

export async function listAgentConversationsPage(
  orgId: string,
  agentId: string,
  options: ListAgentConversationsOptions = {},
): Promise<AgentConversationsPage> {
  const params = new URLSearchParams();
  if (options.scope) params.set('scope', options.scope);
  if (options.search) params.set('search', options.search);
  if (options.sort) params.set('sort', options.sort);
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.offset !== undefined)
    params.set('offset', String(options.offset));
  const query = params.toString();
  const response = await apiRequest<AgentConversationsListResponse>({
    url: `/agents/${orgId}/${agentId}/conversations${query ? `?${query}` : ''}`,
    method: 'GET',
    target: 'platform',
  });
  const data = unwrap<AgentConversationsListResponse>(response);
  const conversations = data?.conversations ?? [];
  return { conversations, total: data?.total ?? conversations.length };
}

// Omitting ``limit`` makes the endpoint return every match, which is what
// the chat sidebars still want; the Chats page asks for a page instead.
export async function listAgentConversations(
  orgId: string,
  agentId: string,
  options: ListAgentConversationsOptions = {},
): Promise<AgentConversationSummary[]> {
  const page = await listAgentConversationsPage(orgId, agentId, options);
  return page.conversations;
}

export async function getAgentConversationDetail(
  orgId: string,
  agentId: string,
  conversationId: string,
  options: { limit?: number } = {},
): Promise<AgentChatMessagesResponse | null> {
  const qs = options.limit ? `?limit=${options.limit}` : '';
  const response = await apiRequest<AgentChatMessagesResponse>({
    url: `/agents/${orgId}/${agentId}/conversations/${conversationId}/messages${qs}`,
    method: 'GET',
    target: 'platform',
  });
  return unwrap<AgentChatMessagesResponse>(response);
}

export async function getAgentConversationMessages(
  orgId: string,
  agentId: string,
  conversationId: string,
  options: { limit?: number } = {},
): Promise<AgentChatMessage[]> {
  const data = await getAgentConversationDetail(
    orgId,
    agentId,
    conversationId,
    options,
  );
  return data?.messages ?? [];
}

export async function persistAssistantTurn(
  orgId: string,
  agentId: string,
  conversationId: string,
  content: string,
  options: { runId?: string; metadata?: Record<string, unknown> } = {},
): Promise<void> {
  await apiRequest({
    url: `/agents/${orgId}/${agentId}/conversations/${conversationId}/assistant-turn`,
    method: 'POST',
    target: 'platform',
    body: {
      content,
      run_id: options.runId,
      metadata: options.metadata ?? {},
    },
  });
}

export type { ContextSwitchKind };

/**
 * What the user had in front of them for a stretch of a conversation, as
 * persisted. The presentational twin of the `surface_context` a turn carries,
 * and deliberately not the same object: surface context is the agent's
 * grounding payload — large, wire-versioned, rebuilt on every canvas edit —
 * while this is the small durable record a human reads back later.
 */
export interface ContextSwitch {
  kind: ContextSwitchKind;
  /** Artifact id, workflow id, or node id, depending on `kind`. */
  id?: string;
  /**
   * Resolved when the switch happens, not when it is read. Ids outlive the
   * things they name — a renamed or deleted artifact still has to read as
   * whatever it was called at the time.
   */
  title?: string;
  /** The workflow a selected or paused node belongs to. */
  workflow_id?: string;
}

/**
 * Mark the point where the focused context changed, for a switch that
 * happened between turns. A switch that arrives with a message is recorded by
 * the chat endpoint off `metadata.chat_context` instead — both dedupe against
 * the conversation's last recorded context, so overlapping calls are no-ops
 * rather than duplicate markers.
 */
export async function recordContextSwitch(
  orgId: string,
  agentId: string,
  conversationId: string,
  context: ContextSwitch,
): Promise<boolean> {
  const response = await apiRequest<{ recorded?: boolean }>({
    url: `/agents/${orgId}/${agentId}/conversations/${conversationId}/context-switch`,
    method: 'POST',
    target: 'platform',
    body: { context },
  });
  // Whether a row was written, or the server already had the conversation
  // in this context. Informational: either way the conversation is there.
  return unwrap<{ recorded?: boolean }>(response)?.recorded ?? false;
}

export async function renameAgentConversation(
  orgId: string,
  agentId: string,
  conversationId: string,
  title: string,
): Promise<void> {
  const response = await apiRequest({
    url: `/agents/${orgId}/${agentId}/conversations/${conversationId}`,
    method: 'PATCH',
    target: 'platform',
    body: { title },
  });
  unwrap(response);
}

export async function deleteAgentConversation(
  orgId: string,
  agentId: string,
  conversationId: string,
): Promise<void> {
  const response = await apiRequest({
    url: `/agents/${orgId}/${agentId}/conversations/${conversationId}`,
    method: 'DELETE',
    target: 'platform',
  });
  unwrap(response);
}
